import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getDisplayPerPage, getIsDirty, setIsDirty } from "store/userSlice";
import { fetchGroups, getGroups } from "store/groupsSlice";
import { api } from "helpers/axios";
import PreviewModal from "components/BalanceScorecard/PreviewModal";
import { fetchFields, getBalancedScorecardFields, getCalculationRelatedFields, getConditionalFields, getFixedFields, getFreeFields, getSystemFields } from "store/fieldsSlice";
import { errorMessage, leaveConfirm, idleCloseConfirm, idleClosedInfo, scrollToTop } from "helpers/common";
import * as Sentry from "@sentry/react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { fetchInterviewsSettings, getInterviewsSettings } from "store/interviewsSettingsSlice";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useWindowDimensions from "hooks/useWindowDimensions";
import Content from "components/BalanceScorecard/Content";
import { useIdleTimer } from "react-idle-timer";
import FixInfo from "components/Calculation/FixInfo";
import EmployeeTableColumn from "components/EmployeeManagement/EmployeeTableColumn";
import { tableResize } from "helpers/tableResize";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";
import ErrorAlert from "components/ui/ErrorAlert";
import ListPagination from "components/ui/ListPagination";
import { useTranslation } from "translation/useTranslation";
import { Button, Input, InputAffix } from "components/ui";

function EvaluatorRow({ fields, calculationRelatedFields, conditionalFields, systemFields, freeFields, balancedScorecardFields, employee, balancedScorecard, setShowPreview, interviews, selectedRow, resizedCount = null }) {
  return <>
    <tr className="tr-separator"></tr>
    <tr data-testid={`row-${balancedScorecard?.hash_id}`} className={`table-row ${selectedRow ? 'selected-row' : ''}`} onClick={() => setShowPreview(employee, balancedScorecard)} data-tooltip-id={employee.exclude_from_payout ? `employee-exclusion-tooltip` : (balancedScorecard.exclude_from_payout ? `balanced-scorecard-exclusion-tooltip` : '')}>
      {
        [...fields, ...calculationRelatedFields, ...conditionalFields, ...systemFields, ...freeFields, ...balancedScorecardFields].map((field, i) => {
          return <EmployeeTableColumn key={i} field={field} employee={employee} displayLkTag={true} balancedScorecards={[balancedScorecard]} resizedCount={resizedCount} />
        })
      }
    </tr>
  </>
}

export default function EvaluatorOverview({ headerTranslation = 'global.menu.evaluation.evaluator_overview', reviewerView = false, listType = null }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();


  const [searchParams, setSearchParams] = useSearchParams();

  const { translate, editableTranslate } = useTranslation();

  const { width } = useWindowDimensions();

  const groups = useSelector(getGroups);
  const displayPerPage = useSelector(getDisplayPerPage);

  const [error, setError] = useState(null);
  const [lockError, setLockError] = useState(null);

  const [sortCols, setSortCols] = useState([['last_name', 'asc']]);
  // The module reviewers' list spans the whole module, so it is paged on the server (LIX-1797).
  const paged = listType === 'module-reviewer';
  const [pagination, setPagination] = useState({ page: 1, perPage: 50, totalPages: 1, totalRows: 0 });
  const resetPage = () => {
    if (paged) setPagination(p => ({ ...p, page: 1 }));
  };
  const changeSortCols = (cols) => {
    setSortCols(cols);
    resetPage();
  };
  const [sortPinCol, setSortPinCol] = useState(null);

  const [triggerColWidth, setTriggerColWidth] = useState(0);

  const [balancedScorecards, setBalancedScorecards] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [searchValue, setSearchValue] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || ''); // term sent to the server

  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedBalancedScorecard, setSelectedBalancedScorecard] = useState(null);

  const [fixMode, setFixMode] = useState(false);
  const [fixData, setFixData] = useState(null);

  const [resizedCount, setResizedCount] = useState(0);

  const globalIsDirty = useSelector(getIsDirty);

  const [contentDirty, setContentDirty] = useState(false);

  const fixedFields = useSelector(getFixedFields);
  const calculationRelatedFields = useSelector(getCalculationRelatedFields);
  const conditionalFields = useSelector(getConditionalFields);
  const systemFields = useSelector(getSystemFields);
  const freeFields = useSelector(getFreeFields);
  const balancedScorecardFields = useSelector(getBalancedScorecardFields);

  const interviews = useSelector(getInterviewsSettings);

  const isFullHD = useMemo(() => width >= 1536, [width]);

  const inlineMode = useMemo(() => isFullHD && selectedBalancedScorecard, [isFullHD, selectedBalancedScorecard]);

  const filteredFixedFields = useMemo(() => {
    if (!fixedFields) return [];
    else if (inlineMode) return fixedFields.filter(f => ['first_name', 'last_name'].includes(f.slug));
    return fixedFields.filter(f => f.is_active && f.display_in.includes('rating_overview'));
  }, [fixedFields, inlineMode]);

  const filteredCalculationRelatedFields = useMemo(() => {
    if (!calculationRelatedFields || (inlineMode)) return [];
    return calculationRelatedFields.filter(f => f.is_active && f.display_in.includes('rating_overview'));
  }, [calculationRelatedFields, inlineMode]);

  const filteredConditionalFields = useMemo(() => {
    if (!conditionalFields || (inlineMode)) return [];
    return conditionalFields.filter(f => f.is_active && f.display_in.includes('rating_overview'));
  }, [conditionalFields, inlineMode]);

  const filteredSystemFields = useMemo(() => {
    if (!systemFields || (inlineMode)) return [];
    return systemFields.filter(f => f.is_active && f.display_in.includes('rating_overview'));
  }, [systemFields, inlineMode]);

  const filteredFreeFields = useMemo(() => {
    if (!freeFields || (inlineMode)) return [];
    return freeFields.filter(f => f.is_active && f.display_in.includes('rating_overview'));
  }, [freeFields, inlineMode]);

  const filteredBalancedScorecardFields = useMemo(() => {
    if (!balancedScorecardFields) return [];
    else {
      let arr = [];
      if (inlineMode) arr = balancedScorecardFields.filter(f => ['lk', 'trend', 'personal_interviews'].includes(f.slug) && f.is_active && f.display_in.includes('rating_overview'));
      else arr = balancedScorecardFields.filter(f => f.is_active && f.display_in.includes('rating_overview'));

      return arr.map(f => {
        return { ...f, key: f.translation_key.replace('field.', '') };
      });
    }
  }, [balancedScorecardFields, inlineMode]);

  const headerRef = useRef(null);
  const floatingSeparatorRef = useRef(null);

  const theadRef = useRef(null);

  const tableRef = useRef(null);

  const fixDivRef = useRef(null);

  const onIdle = () => {
    if (selectedBalancedScorecard) {
      handleClosePreviewModal(null, true);
    }
  };

  const { start, pause } = useIdleTimer({
    onIdle,
    timeout: (1 * 300 * 1000),
    stopOnIdle: true,
    startManually: true
  });

  useEffect(() => {
    if (!groups) dispatch(fetchGroups());

    if (!fixedFields || !freeFields || !balancedScorecardFields) dispatch(fetchFields());

    if (!interviews) dispatch(fetchInterviewsSettings());
  }, []);

  useEffect(() => {
    const theadEl = theadRef.current;
    if (!theadEl) return;

    const headerEl = headerRef.current;
    if (!headerEl) return;

    const floatingEl = floatingSeparatorRef.current;
    if (!floatingEl) return;

    const fixDivEl = fixDivRef.current;

    const parentEl = theadEl.parentElement;
    const headerParentEl = headerEl.parentElement;
    const floatingParentEl = floatingEl.parentElement;

    const handleScrollTable = () => {
      const coordinates = parentEl.getBoundingClientRect();
      const navHeight = document.getElementById('navbar-menu')?.offsetHeight || 0;

      const tabsParentCoordinates = headerParentEl.getBoundingClientRect();
      if (tabsParentCoordinates.y < navHeight) {
        headerEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight + 7}px, 0)`;
        theadEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight + 7}px, 0)`;
        if (fixDivEl) fixDivEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight + 7}px, 0)`;

        floatingParentEl.style.display = 'block';
        floatingEl.style.display = 'block';
        floatingEl.style.transform = `translate3d(0, ${-tabsParentCoordinates.y + navHeight}px, 0)`;
        floatingEl.style.height = (headerEl.offsetHeight + theadEl.offsetHeight + (fixDivEl ? fixDivEl.offsetHeight + 14 : 0) + 14 + 14) + 'px';
      } else {
        headerEl.style.transform = 'translate3d(0,0,0)';
        theadEl.style.transform = 'translate3d(0,0,0)';
        if (fixDivEl) fixDivEl.style.transform = 'translate3d(0,0,0)';

        floatingParentEl.style.display = 'none';
        floatingEl.style.display = 'hidden';
        floatingEl.style.transform = 'translate3d(0,0,0)';
        floatingEl.style.height = '.5rem';
      }
    };
    window.addEventListener('scroll', handleScrollTable);
    return () => window.removeEventListener('scroll', handleScrollTable);
  }, [fixData]);

  // useEffect(() => {
  //   if (displayPerPage > 0) {
  //     fetchEmployees(true);
  //   }
  // }, [displayPerPage]);

  useEffect(() => {
    if (searchParams.get('source') === 'calculation') {
      const budgetHashId = searchParams.get('budget_hash_id');
      const budgetName = searchParams.get('budget_name');
      const errorKey = searchParams.get('error_key');

      setFixData({ hash_id: budgetHashId, budget_name: budgetName, error_key: errorKey });
      setFixMode(true);
    } else {
      setFixMode(false);
      setFixData(null);
    }

    setSearchValue(searchParams.get('search') || '');
    setDebouncedSearch(searchParams.get('search') || '');
  }, [searchParams]);

  useEffect(() => {
    dispatch(setIsDirty(contentDirty));
  }, [contentDirty]);

  useEffect(() => {
    // if (displayPerPage > 0) {
    if (searchParams.get('source') === 'calculation') {
      if (fixData) { // wait for fixData if coming from calculation
        fetchBalancedScorecards(true);
      }
    } else {
      fetchBalancedScorecards(true);
    }
    // }
  }, [debouncedSearch, sortCols, fixData, searchParams.get('source'), pagination.page, pagination.perPage]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchValue);
      if (searchValue !== debouncedSearch) resetPage();
    }, 350);
    return () => clearTimeout(t);
  }, [searchValue]);

  useEffect(() => {
    if (!inlineMode && selectedBalancedScorecard && !showPreviewModal) setShowPreviewModal(true);
    else if (inlineMode && selectedBalancedScorecard && showPreviewModal) {
      setShowPreviewModal(false);
      document.body.style.overflowY = 'auto';
    }
  }, [inlineMode]);

  const maxColWidth = {
    last_name: 300,
    first_name: 300,
    employee_number: 250,
    email: 300,
    individual_pay_factor: 250,
    individual_average_rating: 300,
    exclude_from_payout: 300,
    balanced_scorecard_template: 400,
    lk: 100
  };

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    tableResize(table, 'evaluator_overview', false, {}, maxColWidth, setResizedCount);
  }, [balancedScorecards, selectedBalancedScorecard, triggerColWidth]);

  useEffect(() => {
    setResizedCount(c => c + 1);
  }, [selectedBalancedScorecard]);

  useEffect(() => {
    if (!sortPinCol) setTriggerColWidth(p => p + 1);
  }, [sortCols]);

  const fetchSeq = useRef(0); // only the newest fetch may update the list
  const fetchBalancedScorecards = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    const seq = ++fetchSeq.current;
    let type = listType || (reviewerView ? 'reviewer' : 'evaluator');
    try {
      let url = `/employee-balanced-scorecards/${type}`;
      if (debouncedSearch) url += `?search=${encodeURIComponent(debouncedSearch)}`;
      if (fixData) url += (debouncedSearch ? '&' : '?') + `fix_error_key=${fixData?.error_key}&budget_hash_id=${fixData?.hash_id}`;

      if (sortCols && sortCols.length > 0) {
        url += ((debouncedSearch || fixData) ? '&' : '?') + `sort=` + sortCols.map(sc => sc[0] + ':' + sc[1]).join(',');
      }

      if (paged) url += (url.includes('?') ? '&' : '?') + `page=${pagination.page}&limit=${pagination.perPage}`;

      const { data, status } = await api.get(url);
      if (seq !== fetchSeq.current) return; // a newer fetch superseded this one

      if (status === 200) {
        setLoading(false);

        const newData = data.data.map((item) => {
          const information = {};

          item.employee.employeeInformation.data.forEach(info => {
            information[info.key] = info.value;
          });

          // item.employee.information = information;
          item.employee.information = item.employee.information || information;

          return item;
        });

        setBalancedScorecards(newData);

        const meta = data.meta?.pagination;
        if (paged && meta) {
          // a reload can leave the page past the end, e.g. after the list shrank
          setPagination(p => ({ ...p, page: Math.min(p.page, Math.max(+meta.total_pages, 1)), totalPages: +meta.total_pages, totalRows: +meta.total }));
        }
        setError(null);
      }
    } catch (err) {
      if (seq !== fetchSeq.current) return;
      setLoading(false);
      setError(errorMessage(err, 'fetch employees'));
      Sentry.captureException(err);
    }
  };

  const handleChangeSortCol = (field) => {
    const newSortCols = [...sortCols];
    let found = false;
    for (let i = 0; i < newSortCols.length; i++) {
      if (newSortCols[i][0] === field.slug) {
        newSortCols[i][1] = (newSortCols[i][1] === 'asc' ? 'desc' : 'asc');
        found = true;
        break;
      }
    }
    if (!found) {
      if (newSortCols.length > 1 || !sortPinCol) newSortCols.pop(); // max 2, so if > 1 or no pin, we remove the last one first before push
      newSortCols.push([field.slug, 'asc']);
    }
    changeSortCols(newSortCols);
  };

  const getSortCol = (field) => {
    for (const sc of sortCols) {
      if (sc[0] === field.slug) return sc;
    }
    return null;
  };

  const handleSetSortPinCol = (field) => {
    if (sortPinCol === field.slug) {
      setSortPinCol(null);
      changeSortCols([sortCols[0]]);
    } else {
      setSortPinCol(field.slug);
      changeSortCols(sortCols.filter(sc => sc[0] === field.slug));
    }
  };

  const handleBtnFilter = () => {
    setShowFilter(!showFilter);
  };

  const handleCancelFilter = () => {
    setShowFilter(false);
  };

  const handleSearchFilter = (e) => {
    e?.preventDefault();
    setSearchParams({
      search: searchValue
    });
  };

  const fetchEmployee = async (hashId = null, balancedScorecard = null) => {
    if (!hashId && !selectedEmployee) return;

    setError(null);
    try {
      const { data, status } = await api.get(`/employees/${hashId || selectedEmployee.hash_id}`);
      if (status === 200) {
        const information = {};
        data.employeeInformation.data.forEach(info => {
          information[info.key] = info.value;
        });
        setSelectedEmployee({ ...data, information });
        if (balancedScorecard) setSelectedBalancedScorecard(balancedScorecard);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch employee'));
      Sentry.captureException(err);
    }
  };

  const postLock = async (hashId) => {
    if (reviewerView) return true;

    setLockError(null);
    try {
      const { status } = await api.post(`/employee-balanced-scorecards/${hashId}/lock`);
      if (status === 200) return true;
    } catch (err) {
      if (err?.response?.status === 403) {
        let formatted = translate('evaluation.balanced_scorecard_lock_forbidden');
        if (err?.response?.data) {
          const errData = err?.response?.data;
          if (errData?.data?.evaluator) {
            formatted = formatted.replace('$X', '<b>' + errData?.data?.evaluator?.name + '</b>');
          }
        }
        setLockError(formatted);
      } else {
        setLockError(errorMessage(err, 'lock employee balanced scorecard'));
      }
    }
  };

  const postUnlock = async (hashId) => {
    if (reviewerView) return true;

    setLockError(null);
    try {
      const { status } = await api.post(`/employee-balanced-scorecards/${hashId}/unlock`);
      if (status === 200) return true;
    } catch (err) {
      if (err?.response?.status === 403) {
        let formatted = translate('evaluation.balanced_scorecard_lock_forbidden');
        if (err?.response?.data) {
          const errData = err?.response?.data;
          if (errData?.data?.evaluator) {
            formatted = formatted.replace('$X', '<b>' + errData?.data?.evaluator?.name + '</b>');
          }
        }
        setLockError(formatted);
      } else {
        setLockError(errorMessage(err, 'unlock employee balanced scorecard'));
      }
    }
  };

  const setShowPreview = async (employee, balancedScorecard) => {
    if (globalIsDirty) {
      const confirmed = await leaveConfirm();
      if (!confirmed) return;
      else dispatch(setIsDirty(false));
    }

    if (selectedBalancedScorecard) { // old/currently opened
      await postUnlock(selectedBalancedScorecard.hash_id);

      setShowPreviewModal(false);
      setSelectedEmployee(null);
      setSelectedBalancedScorecard(null);
      document.body.style.overflowY = 'auto';
    }

    if (await postLock(balancedScorecard.hash_id)) { // new one // check if it's already locked by someone else
      if (!isFullHD) setShowPreviewModal(true);
      fetchEmployee(employee.hash_id, balancedScorecard);
      scrollToTop();
      if (!reviewerView) start();
    } else if (!reviewerView) {
      pause();
    }
  };

  const handleClosePreviewModal = async (e, idle = false) => {
    let notifyIdle = idle;
    if (globalIsDirty) {
      const choice = idle ? await idleCloseConfirm() : ((await leaveConfirm()) ? 'close' : 'continue');
      if (choice === 'continue') {
        if (!reviewerView) start(); // idle timer stops on idle, restart it so the lock is still released later
        return;
      } else dispatch(setIsDirty(false));
      if (choice === 'close') notifyIdle = false; // the user closed it, no need to tell them
    }

    if (!reviewerView) pause();

    await postUnlock(selectedBalancedScorecard.hash_id);

    setShowPreviewModal(false);
    setSelectedEmployee(null);
    setSelectedBalancedScorecard(null);
    document.body.style.overflowY = 'auto';

    if (notifyIdle) idleClosedInfo(globalIsDirty);
  };

  const handleFixGoBack = (e) => {
    e.preventDefault();

    navigate({ pathname: '/calculation/preliminary-check', search: `?hash_id=${fixData?.hash_id}` });
  };

  const handleFixCancel = (e) => {
    e.preventDefault();

    setSearchParams({});
  };

  return (
    <>
      <div className="col d-flex">
        <div className={`bg-white w-100 p-4 content-card min-height-after-navbar`}>
          {error ?
            <div>
              <ErrorAlert error={error} onClose={() => setError(null)} />
            </div> :

            <div>
              <div className={inlineMode ? 'max-w-500' : ''}>
                <div className="position-relative" style={{ display: 'none' }}>
                  <div ref={floatingSeparatorRef} className="floating-separator"></div>
                </div>
                <div ref={headerRef} className="row position-relative z-3">
                  {
                    !fixMode && <>
                      <div className="col-auto align-self-center">
                        <h4 className="mb-0">{editableTranslate(headerTranslation)}</h4>
                      </div>
                      <div className="col">
                        <div className="d-flex justify-content-end">
                          <form className="form-inline" onSubmit={handleSearchFilter}>
                            <InputAffix className={searchValue ? 'bg-red-dark-soft' : ''} prefix={<i className="bi bi-search mr-2"></i>}>
                              <Input data-testid="search-input" type="text" value={searchValue} onChange={e => setSearchValue(e.target.value)} placeholder={translate('form.search')} autoComplete="false" maxLength={255} />
                            </InputAffix>
                          </form>

                          {/* <div className="btn-group">
                        <Button variant="secondary" className="ml-3" icon="bi-filter" onClick={handleBtnFilter} data-testid="filter-button">{editableTranslate('form.filter')} <i className="bi bi-chevron-down"></i></Button>
                        <div className={`dropdown-menu dropdown-menu-right p-3 filter-dropdown ${showFilter ? 'show' : ''}`}>
                          <div>
                            <label>{editableTranslate('field.group')}</label>
                            <NativeSelect>
                              <option value="-">alle</option>
                              {
                                groups && groups.map((group, i) => {
                                  return <option key={i} value={group.hash_id}>{group.name}</option>
                                })
                              }
                            </NativeSelect>
                          </div>
                          <div className="mt-3">
                            <Button variant="primary" data-testid="apply-button">{editableTranslate('button.apply')}</Button> <Button variant="secondary" onClick={handleCancelFilter} data-testid="cancel-button">{editableTranslate('button.cancel')}</Button>
                          </div>
                        </div>
                      </div> */}
                        </div>
                      </div>
                    </>
                  }
                </div>
              </div>

              {
                fixMode && <div ref={fixDivRef} className="position-relative z-3">
                  <div className="text-right mb-3">
                    <Button variant="secondary" onClick={handleFixGoBack} data-testid="back-button"><i className="bi bi-arrow-left"></i> {editableTranslate('calculation_overview.go_back')}</Button>
                    <Button variant="secondary" onClick={handleFixCancel} data-testid="cancel-button" className="ml-2">{editableTranslate('button.cancel')}</Button>
                  </div>

                  <FixInfo budgetName={fixData?.budget_name} errorKey={fixData?.error_key} />
                </div>
              }

              <div className="mt-3">
                <div className={inlineMode ? 'evaluator-inline-mode' : ''}>
                  <div className="evaluator-left-div">
                    {
                      lockError && <div>
                        <ErrorAlert error={lockError} isHtml={true} onClose={() => setLockError(null)} />
                      </div>
                    }
                    <div className="table-responsive has-custom-scrollbar">
                      <table ref={tableRef} className="table evaluation-overview-table" data-testid="balanced-scorecards-table">
                        <thead ref={theadRef} className="sticky-top">
                          <tr>
                            {
                              [...filteredFixedFields, ...filteredCalculationRelatedFields, ...filteredConditionalFields, ...filteredSystemFields].map((field, i) => {
                                const sc = getSortCol(field);

                                return <th key={i} scope="col" className={`cursor-pointer ${field.alignment ? 'text-' + field.alignment : ''}`}>
                                  <span className="float-right floating-icons">
                                    <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${sc && sc[0] === field.slug ? (sc[1] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol(field)} data-testid={`sort-${field.slug}`} />
                                    {
                                      (sc && sc[0] === field.slug) && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === field.slug ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol(field)} data-testid={`sort-pin-${field.slug}`} />
                                    }
                                  </span>
                                  <span data-testid={`sort-label-${field.slug}`} className="th-span" onClick={() => handleChangeSortCol(field)}>
                                    {field?.custom_name || editableTranslate(field.translation_key, field.name)}
                                    <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                                    <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                                  </span>
                                  <div className="th-resize-div" data-field={field.slug}></div>
                                </th>
                              })
                            }
                            {
                              filteredFreeFields.map((field, i) => {
                                const sc = getSortCol(field);

                                return <th key={i} scope="col" className="cursor-pointer">
                                  <span className="float-right floating-icons">
                                    <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${sc && sc[0] === field.slug ? (sc[1] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol(field)} data-testid={`sort-${field.slug}`} />
                                    {
                                      (sc && sc[0] === field.slug) && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === field.slug ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol(field)} data-testid={`sort-pin-${field.slug}`} />
                                    }
                                  </span>
                                  <span data-testid={`sort-label-${field.slug}`} className="th-span" onClick={() => handleChangeSortCol(field)}>
                                    {field?.custom_name || editableTranslate(field.translation_key, field.name)}
                                    <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                                    <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                                  </span>
                                  <div className="th-resize-div" data-field={field.slug}></div>
                                </th>
                              })
                            }
                            {
                              filteredBalancedScorecardFields.map((field, i) => {
                                const sc = getSortCol(field);

                                return <th key={i} scope="col" className={`cursor-pointer ${field.alignment ? 'text-' + field.alignment : ''}`}>
                                  <span className="float-right floating-icons">
                                    <Button variant="ghost" size="inline" className="p-0 ml-1" icon={`bi-bold ${sc && sc[0] === field.slug ? (sc[1] === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down') : 'bi-arrow-up v-hidden'}`} onClick={() => handleChangeSortCol(field)} data-testid={`sort-${field.slug}`} />
                                    {
                                      (sc && sc[0] === field.slug) && <Button variant="ghost" size="inline" className="position-relative z-2 p-0 ml-1" icon={`bi-bold ${sortPinCol === field.slug ? 'bi-pin-angle-fill text-primary' : 'bi-pin-angle'}`} onClick={() => handleSetSortPinCol(field)} data-testid={`sort-pin-${field.slug}`} />
                                    }
                                  </span>
                                  <span data-testid={`sort-label-${field.slug}`} className="th-span" onClick={() => handleChangeSortCol(field)}>
                                    {field?.custom_name || editableTranslate(field.translation_key, field.name)}
                                    {field.name === 'Trend' && <>
                                      <i className="bi bi-info-circle ml-1" data-tooltip-id={`th-trend-tooltip`}></i>
                                      <ReactTooltip id={`th-trend-tooltip`} place="bottom"
                                        content={editableTranslate('tooltips.field.trend.tooltip')} clickable />
                                    </>}
                                    <i className={`bi bi-bold bi-arrow-up invisible ml-1`}></i>
                                    <i className={`bi bi-bold bi-pin-angle-fill invisible ml-1`}></i>
                                  </span>
                                  <div className="th-resize-div" data-field={field.slug}></div>
                                </th>
                              })
                            }
                          </tr>
                        </thead>
                        <tbody>
                          {
                            !loading && Array.isArray(balancedScorecards) && balancedScorecards.map((item, i) => {
                              return <EvaluatorRow key={'row_' + i} fields={filteredFixedFields} calculationRelatedFields={filteredCalculationRelatedFields} conditionalFields={filteredConditionalFields} systemFields={filteredSystemFields} freeFields={filteredFreeFields} balancedScorecardFields={filteredBalancedScorecardFields} employee={item.employee} balancedScorecard={item} setShowPreview={setShowPreview} interviews={interviews} selectedRow={selectedBalancedScorecard?.hash_id === item.hash_id} resizedCount={resizedCount} />
                            })
                          }

                          {
                            (loading || !balancedScorecards) && <tr>
                              <td colSpan={40}>
                                <div className="d-flex justify-content-center" data-testid="loading">
                                  <div className="spinner-border" role="status">
                                    <span className="sr-only">Wird geladen...</span>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          }

                          {
                            !loading && Array.isArray(balancedScorecards) && balancedScorecards.length === 0 && <tr>
                              <td colSpan={40} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data')}</td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                    <TableScrollbar tableRef={tableRef} data={balancedScorecards} trigger={resizedCount} />
                    {
                      paged && <ListPagination
                        page={pagination.page}
                        perPage={pagination.perPage}
                        totalPages={pagination.totalPages}
                        totalRows={pagination.totalRows}
                        setPage={(page) => setPagination(p => ({ ...p, page }))}
                        setPerPage={(perPage) => setPagination(p => ({ ...p, page: 1, perPage }))}
                      />
                    }
                  </div>
                  {
                    inlineMode && <div className="evaluator-right-div">
                      <div className="evaluator-header">
                        <h5 className="font-weight-bold">{editableTranslate('global.software_name', 'PEB')} {editableTranslate('global.menu.settings.balance_scorecard')} - {selectedBalancedScorecard?.balanced_scorecard_setting?.name || editableTranslate('balanced_scorecard.management')}</h5>
                        <span className="cursor-pointer font-weight-bold balanced-scorecard-close" onClick={handleClosePreviewModal} data-testid="close-button"><i className="bi bi-x-lg"></i></span>
                      </div>

                      <Content show={selectedBalancedScorecard ? true : false} employee={selectedEmployee} balancedScorecard={selectedBalancedScorecard} fetchBalancedScorecards={fetchBalancedScorecards} reviewerView={reviewerView} allowReportEmail={listType !== 'module-reviewer'} setContentDirty={setContentDirty} fetchEmployee={fetchEmployee} />
                    </div>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      </div>

      {
        !inlineMode && <PreviewModal show={showPreviewModal} handleClose={handleClosePreviewModal} employee={selectedEmployee} balancedScorecard={selectedBalancedScorecard} fetchBalancedScorecards={fetchBalancedScorecards} reviewerView={reviewerView} allowReportEmail={listType !== 'module-reviewer'} setContentDirty={setContentDirty} />
      }
      <ReactTooltip id={`employee-exclusion-tooltip`} content={translate('tooltips.evaluation.balanced_scorecard.employee_exclude_from_payout_tooltip')} />
      <ReactTooltip id={`balanced-scorecard-exclusion-tooltip`} content={translate('tooltips.evaluation.balanced_scorecard.exclude_from_payout_tooltip')} />
    </>
  );
}
